<template>
  <Dialog
    v-model:visible="isVisible"
    modal
    header="Create Separate Player Login"
    :style="{ maxWidth: '500px' }"
    @show="handleShow"
  >
    <div class="space-y-4">
      <p class="text-sm text-gray-600">
        This player currently shares a login with their parent or guardian
        <strong>{{ contactName }}</strong
        >. This will create a separate account for the player with their own
        email and password.
      </p>

      <div class="rounded-lg bg-amber-50 border border-amber-200 p-3">
        <div class="flex items-start gap-2">
          <i class="pi pi-info-circle text-amber-600 mt-0.5" />
          <p class="text-sm text-amber-800">
            After splitting, the player will need to log in with their new
            email. Any active mobile app sessions will need to be
            re-authenticated.
          </p>
        </div>
      </div>

      <div class="space-y-2">
        <label for="playerEmail" class="text-sm font-medium text-gray-700">
          Email for player's account
        </label>
        <InputText
          id="playerEmail"
          v-model="playerEmail"
          type="email"
          class="w-full"
          placeholder="player@example.com"
        />
        <small v-if="validationError" class="text-red-500">
          {{ validationError }}
        </small>
        <small v-if="apiError" class="text-red-500">
          {{ apiError }}
        </small>
      </div>

      <div class="flex justify-end gap-2 pt-2">
        <Button
          label="Cancel"
          severity="secondary"
          outlined
          @click="isVisible = false"
        />
        <Button
          label="Create Separate Login"
          :loading="isSubmitting"
          @click="handleSubmit"
        />
      </div>
    </div>
  </Dialog>
</template>

<script setup lang="ts">
import { ref } from "vue";
import Dialog from "primevue/dialog";
import InputText from "primevue/inputtext";
import Button from "primevue/button";
import { useToast } from "primevue/usetoast";
import { splitSharedAccount } from "../api";

interface Props {
  readonly profileId: number;
  readonly profileName: string;
  readonly currentEmail: string;
  readonly contactName: string;
}

const props = defineProps<Props>();

const isVisible = defineModel<boolean>({ required: true });

const emit = defineEmits<{
  accountSplit: [];
}>();

const toast = useToast();
const playerEmail = ref("");
const isSubmitting = ref(false);
const validationError = ref("");
const apiError = ref("");

function generateSuggestedEmail(): string {
  const email = props.currentEmail;
  if (!email) return "";

  const [localPart, domain] = email.split("@");
  if (!localPart || !domain) return "";

  const slug =
    (props.profileName || "player")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "")
      .substring(0, 20) || "player";

  return `${localPart}+${slug}@${domain}`;
}

function validate(): boolean {
  validationError.value = "";

  if (!playerEmail.value.trim()) {
    validationError.value = "Email is required";
    return false;
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(playerEmail.value.trim())) {
    validationError.value = "Please enter a valid email address";
    return false;
  }

  return true;
}

async function handleSubmit() {
  if (!validate()) return;

  apiError.value = "";
  isSubmitting.value = true;

  try {
    const result = await splitSharedAccount(
      props.profileId,
      playerEmail.value.trim()
    );

    if ("error" in result) {
      apiError.value = result.error as string;
      return;
    }

    toast.add({
      severity: "success",
      summary: "Account Split",
      detail: `Separate login created for ${props.profileName}`,
      life: 5000,
    });

    isVisible.value = false;
    emit("accountSplit");
  } catch {
    apiError.value = "An unexpected error occurred";
  } finally {
    isSubmitting.value = false;
  }
}

function handleShow() {
  playerEmail.value = generateSuggestedEmail();
  validationError.value = "";
  apiError.value = "";
}
</script>
